import React, { useState } from "react";
import { Trash2, HelpCircle, Copy, Check, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { APPEARANCE_TYPES } from "@/lib/aidPlannerSchema";
import {
  findClaimAvailabilityRow,
  formatClaimAvailabilityLabel,
  formatClaimedToDateLabel,
  formatClaimFeeLabel,
  getClaimAvailabilityTable,
  getClaimedToDateAmount,
  getPlannerAppearanceOptions,
  getClaimTrackerPreviewRows,
  claimMatchesTrackerRow,
} from "@/lib/aidPlannerCriminalFees";
import {
  mapGoogleCalendarEventsToAppearanceClaims,
} from "@/lib/aidPlannerAppearanceImport";
import {
  listGoogleCalendarEvents,
} from "@/lib/googleCalendarClient";
import { SelectField } from "./PlannerFields";

export default function AppearanceClaimsTile({
  planner,
  addAppearanceClaim,
  updateAppearanceClaim,
  removeAppearanceClaim,
  importAppearanceClaims,
}) {
  const claims = planner.appearanceClaims || [];
  const [importError, setImportError] = useState("");
  const [googleAccessToken] = useState(() => window.sessionStorage.getItem("aidPlannerGoogleCalendarAccessToken") || "");
  const [googleCalendars] = useState(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem("aidPlannerGoogleCalendars") || "[]");
    } catch {
      return [];
    }
  });
  const [selectedGoogleCalendarId] = useState(() => window.sessionStorage.getItem("aidPlannerGoogleSelectedCalendarId") || "");
  const [googleDateFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.toISOString().slice(0, 10);
  });
  const [googleDateTo] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    return date.toISOString().slice(0, 10);
  });
  const [isImportingGoogle, setIsImportingGoogle] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState("");
  const [showTemplateHelp, setShowTemplateHelp] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [expandedScopes, setExpandedScopes] = useState({});
  
  const toggleScope = (scopeName) => {
    setExpandedScopes((prev) => ({
      ...prev,
      [scopeName]: !prev[scopeName],  // false/undefined → expand, true → collapse
    }));
  };

  const isAidApproved =
    planner.aid?.applicationStatus === "Aid granted" ||
    planner.aid?.applicationStatus === "Approved" ||
    planner.aid?.applicationStatus === "Approved with a caveat" ||
    planner.aid?.applicationStatus === "Approved with a contribution" ||
    planner.application?.decisionResult === "Granted" ||
    planner.funding?.grantInPlace;

  const appearanceOptions =
    planner.matter.matterType === "Criminal"
      ? [...new Set([...getPlannerAppearanceOptions(planner.matter.court, planner), "Other"])]
      : APPEARANCE_TYPES;

  const claimAvailabilityRows = getClaimAvailabilityTable(planner);
  const previewClaimRows = getClaimTrackerPreviewRows(planner);
  const visibleClaimRows = claimAvailabilityRows.length > 0 ? claimAvailabilityRows : previewClaimRows;

  const grantTypeLabel = Array.isArray(planner.aid?.aidTypeList) && planner.aid.aidTypeList.length > 0
    ? planner.aid.aidTypeList.join("; ")
    : planner.aid?.aidType || "";

  const claimedToDateTotal = visibleClaimRows.reduce((sum, row) => sum + getClaimedToDateAmount(row), 0);
  const claimedToDateTotalLabel = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(claimedToDateTotal);

  const getEffectiveDateWarning = (claim) => {
    if (!planner.aid?.effectiveDate || !claim?.date) return "";
    if (claim.date >= planner.aid.effectiveDate) return "";
    return "The aid effective date is after your appearance. You cannot claim this appearance with your current grant. You may need to apply for a retrospective grant to cover this claim.";
  };

  const handleGoogleImport = async () => {
    setIsImportingGoogle(true);
    setImportError("");
    setCalendarStatus("");
    try {
      const events = await listGoogleCalendarEvents(
        googleAccessToken,
        selectedGoogleCalendarId,
        googleDateFrom,
        googleDateTo
      );
      const calendarLabel =
        googleCalendars.find((item) => item.id === selectedGoogleCalendarId)?.summary || "Google";
      const mapped = mapGoogleCalendarEventsToAppearanceClaims(events, calendarLabel, planner);
      importAppearanceClaims(mapped);
      setCalendarStatus(
        mapped.length > 0
          ? `Imported ${mapped.length} Google event${mapped.length === 1 ? "" : "s"} from ${calendarLabel} that match this matter.`
          : `No Google events in ${calendarLabel} matched this matter's client and aid details.`
      );
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Could not import Google Calendar events.");
    } finally {
      setIsImportingGoogle(false);
    }
  };

  const calendarTemplateText = [
    `--- AID PLANNER TEMPLATE ---`,
    `Client: ${planner.client?.fullName || "CLIENT NAME"}`,
    `Aid Number: ${planner.aid?.aidNumber || ""}`,
    `Type: ${planner.matter?.appearanceType || "MENTION"}`,
    `Outcome: `,
    `Units: `,
    `----------------------------`
  ].join("\n");

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(calendarTemplateText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden border-t-4 border-t-purple-600 flex flex-col h-full">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Appearances and Claims</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTemplateHelp(!showTemplateHelp)}
            className={`p-1.5 rounded-lg transition-colors ${showTemplateHelp ? "bg-purple-100 text-purple-700" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"}`}
            title="Calendar Entry Template Help"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          {googleAccessToken && (
            <button
              type="button"
              onClick={handleGoogleImport}
              disabled={isImportingGoogle}
              className="rounded-lg bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-50"
            >
              {isImportingGoogle ? "Syncing..." : "Sync Calendar"}
            </button>
          )}
        </div>
      </div>

      <div className="p-5 flex-1 space-y-4">
        {showTemplateHelp && (
          <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-purple-900 flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4" />
                  Calendar Sync Template
                </h3>
                <p className="text-xs text-purple-700 mb-3 leading-relaxed">
                  To ensure your appearances and claims sync perfectly from <b>Google Calendar, Outlook, or Gmail</b>, 
                  simply paste this template into the <b>description/notes</b> field of your calendar event. 
                  When you sync, the system will automatically extract the Type, Outcome, and Units.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCopyTemplate}
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-purple-200 rounded-lg text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors shadow-sm whitespace-nowrap"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy Template"}
              </button>
            </div>
            <pre className="bg-white border border-purple-100 rounded-lg p-3 text-xs font-mono text-slate-600 overflow-x-auto whitespace-pre">
              {calendarTemplateText}
            </pre>
          </div>
        )}

        {calendarStatus && (
          <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            {calendarStatus}
          </div>
        )}
        {importError && (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {importError}
          </div>
        )}

        {!isAidApproved ? (
          <p className="text-sm text-slate-500 py-6 text-center italic">This section will display once aid is approved/granted.</p>
        ) : (
          <div className="space-y-4">
            {(claimAvailabilityRows.length > 0 || planner.matter.matterType === "Criminal") && (() => {
              const scopes = [...new Set(visibleClaimRows.map((r) => r.table))];
              return (
                <div className="space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">Claim Allocation Tracker</h4>
                      {grantTypeLabel && (
                        <p className="text-xs font-semibold text-purple-700 mt-0.5">Active scope: {grantTypeLabel}</p>
                      )}
                    </div>
                  </div>

                  {scopes.map((scopeName) => {
                    const scopeRows = visibleClaimRows.filter((r) => r.table === scopeName);
                    if (scopeRows.length === 0) return null;

                    const isExpanded = expandedScopes[scopeName] === true;

                    // Title lookup: explicit overrides for legacy names, then strip "Table X – " prefix
                    const SECTION_TITLE_MAP = {
                      "Single Summary grant": "Single Summary (Table A)",
                      "Table B – Bail Magistrates'/Children's Court": "Bail — Magistrates'/Children's Court (Table B)",
                      "Bail grant (CRSM60)": "Bail — Magistrates'/Children's Court (Table B)", // legacy fallback
                      "Psychological Assessment (DISB51)": "Psychological — In-Custody Assessment (DISB51)",
                      "County Court Breach grant": "County Court Breach (Table J)",
                      "Consolidated Extension (CRSM8)": "Consolidated Extension – Uplift Fees", // legacy key fallback
                      "Consolidated Extension – Uplift Fees": "Consolidated Extension – Uplift Fees",
                      "Table A4 – Family Violence Protection Act": "Family Violence — Intervention Orders (Table A4)",
                      "Table A5 – Personal Safety Intervention Order Act": "Personal Safety Intervention Orders (Table A5)",
                      "Table C – County Court Bail": "Bail — County Court (Table C)",
                      "Table D – Supreme Court Bail": "Bail — Supreme Court (Table D)",
                      "Table E – Indictable Committal": "Indictable — Committal Stage (Table E)",
                      "Table F – County Court Indictable": "County Court — Indictable (Table F)",
                      "Table F – Supreme Court Indictable": "Supreme Court — Indictable (Table F)",
                      "Table F(i) – Children's Court Indictable": "Children's Court — Serious Indictable (Table F(i))",
                      "Table G – County Court Sentence Appeal": "County Court — Sentence Appeal (Table G)",
                      "Table H – County Court Conviction Appeal": "County Court — Conviction Appeal (Table H)",
                      "Table J – Supreme Court Breach": "Supreme Court — Breach (Table J)",
                      "Table K – Court of Appeal": "Court of Appeal (Table K)",
                      "Table K(i) – Court of Appeal DPP": "Court of Appeal — DPP (Table K(i))",
                      "Table K(ii) – Interlocutory Appeal": "Interlocutory Appeal (Table K(ii))",
                      "Table L – High Court": "High Court (Table L)",
                      "Table T – Mental Impairment Act (County Court)": "Mental Impairment Act — County Court (Table T)",
                      "Table T – Mental Impairment Act (Supreme Court)": "Mental Impairment Act — Supreme Court (Table T)",
                      "Table Y – Chief Examiner": "Chief Examiner / Witness Assistance (Table Y)",
                      "Table Z – Serious Offenders Act (County Court)": "Serious Offenders Act — County Court (Table Z)",
                      "Table Z – Serious Offenders Act (Supreme Court)": "Serious Offenders Act — Supreme Court (Table Z)",
                    };
                    const title = SECTION_TITLE_MAP[scopeName] ||
                      scopeName.replace(/^Table\s+[A-Za-z0-9()()]+\s+[–\-]\s+/, "");

                    const scopeClaimedTotal = scopeRows.reduce((sum, row) => sum + getClaimedToDateAmount(row), 0);
                    const scopeClaimedTotalLabel = new Intl.NumberFormat("en-AU", {
                      style: "currency",
                      currency: "AUD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(scopeClaimedTotal);

                    return (
                      <div key={scopeName} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                        <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-3">
                            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
                            <button
                              type="button"
                              onClick={() => toggleScope(scopeName)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 hover:bg-purple-100 hover:text-purple-800 active:bg-purple-200 rounded-md transition-all border border-purple-100/80 shadow-sm"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronDown className="w-3.5 h-3.5 text-purple-600" strokeWidth={3} />
                                  <span>Collapse</span>
                                </>
                              ) : (
                                <>
                                  <ChevronRight className="w-3.5 h-3.5 text-purple-600" strokeWidth={3} />
                                  <span>Expand</span>
                                </>
                              )}
                            </button>
                            <span className="text-[10px] bg-slate-200/70 text-slate-700 font-bold px-2 py-0.5 rounded-full">
                              {scopeRows.length} item{scopeRows.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 font-bold">
                            Claimed: <span className="font-mono text-slate-900">{scopeClaimedTotalLabel}</span>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs text-left border-collapse">
                              <thead>
                                <tr className="bg-slate-50/30 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold">
                                  <th className="px-4 py-2.5 font-semibold w-[35%]">Appearance type</th>
                                  <th className="px-4 py-2.5 font-semibold">Available</th>
                                  <th className="px-4 py-2.5 font-semibold">Fee</th>
                                  <th className="px-4 py-2.5 font-semibold">Claimed</th>
                                  <th className="px-4 py-2.5 font-semibold">Remaining</th>
                                  <th className="px-4 py-2.5 font-semibold">Total Claimed</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {scopeRows.map((row) => {
                                  const isLow = row.remaining <= 1;
                                  const isAppearanceUnit = ["appearance", "day", "mention", "attendance"].includes(row.allocationUnit);
                                  const maxInputs = Math.min(row.allocationCount || 20, 20);
                                  const matchingClaims = claims.filter((claim) => claimMatchesTrackerRow(claim, row));

                                  return (
                                    <React.Fragment key={row.feeType}>
                                      <tr className={`${isLow ? "bg-amber-50/70" : ""} hover:bg-slate-50/40`}>
                                        <td className="px-4 py-3 font-medium text-slate-900 break-words">{row.appearanceType}</td>
                                        <td className="px-4 py-3 text-slate-600">{formatClaimAvailabilityLabel(row)}</td>
                                        <td className="px-4 py-3 text-slate-600 font-mono">{formatClaimFeeLabel(row)}</td>
                                        <td className="px-4 py-3 text-slate-600 font-mono">{row.claimed}</td>
                                        <td className={`px-4 py-3 font-bold ${isLow ? "text-amber-700" : "text-slate-900"}`}>
                                          {row.remainingLabel}
                                        </td>
                                        <td className="px-4 py-3 text-slate-900 font-bold whitespace-nowrap font-mono">{formatClaimedToDateLabel(row)}</td>
                                      </tr>
                                      {maxInputs > 0 && (
                                        <tr className="bg-slate-50/30 border-b border-slate-100">
                                          <td colSpan={6} className="px-4 py-2">
                                            <div className="space-y-2 pl-4 py-1">
                                              {matchingClaims.map((claim, i) => {
                                                const value = claim?.date || "";
                                                const warningMsg = getEffectiveDateWarning(claim);
                                                return (
                                                  <div key={claim.id} className="flex flex-col gap-1.5">
                                                    <div className={`flex flex-wrap items-center gap-3 py-1 ${i > 0 ? "border-t border-slate-200/60 pt-2" : ""}`}>
                                                      <span className="text-[11px] font-bold text-slate-400 w-16 text-right">{isAppearanceUnit ? "App." : "Claim"} {i + 1}:</span>
                                                      <input
                                                        type="date"
                                                        value={value}
                                                        onChange={(e) => {
                                                          const newDate = e.target.value;
                                                          if (!newDate && !claim.notes && !claim.invoiceFileName) {
                                                            removeAppearanceClaim(claim.id);
                                                          } else {
                                                            updateAppearanceClaim(claim.id, "date", newDate);
                                                          }
                                                        }}
                                                        className="h-8 w-[130px] rounded-lg border border-slate-200 bg-white px-2.5 text-xs shadow-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                                                      />
                                                      {row.allocationUnit === "kilometre" && (
                                                        <input
                                                          type="number"
                                                          placeholder="km"
                                                          title="Kilometres claimed"
                                                          value={claim.claimedUnits || ""}
                                                          onChange={(e) => updateAppearanceClaim(claim.id, "claimedUnits", e.target.value)}
                                                          className="h-8 w-[70px] rounded-lg border border-slate-200 bg-white px-2.5 text-xs shadow-sm focus:border-purple-500 outline-none"
                                                        />
                                                      )}
                                                      <input
                                                        type="text"
                                                        placeholder="Notes / Outcome..."
                                                        value={claim.notes || ""}
                                                        onChange={(e) => updateAppearanceClaim(claim.id, "notes", e.target.value)}
                                                        className="h-8 flex-1 min-w-[150px] rounded-lg border border-slate-200 bg-white px-3 text-xs shadow-sm focus:border-purple-500 outline-none"
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() => removeAppearanceClaim(claim.id)}
                                                        className="text-slate-400 hover:text-rose-600 p-1 rounded-md"
                                                        title={`Clear appearance`}
                                                      >
                                                        <Trash2 className="w-4 h-4" />
                                                      </button>
                                                    </div>
                                                    {warningMsg && (
                                                      <div className="text-[10px] text-amber-600 font-bold bg-amber-50 border border-amber-100 rounded px-2.5 py-1 ml-16 max-w-xl">
                                                        {warningMsg}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                              {matchingClaims.length < maxInputs && (
                                                <div className={`${matchingClaims.length > 0 ? "border-t border-slate-200/50 pt-2" : ""} py-1 pl-[76px]`}>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      addAppearanceClaim({
                                                        date: "",
                                                        appearanceType: row.appearanceType,
                                                        feeType: row.feeType,
                                                        claimLodged: false,
                                                      });
                                                    }}
                                                    className="text-xs font-bold text-purple-600 hover:text-purple-700 transition-colors"
                                                  >
                                                    + Add {isAppearanceUnit ? "appearance" : "claim"}
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Overall Tracker Total Summary Box */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs font-bold text-slate-700 shadow-sm">
                    <span>Total Approved Funds Claimed</span>
                    <span className="text-sm font-mono text-slate-900 font-bold">{claimedToDateTotalLabel}</span>
                  </div>
                </div>
              );
            })()}

            {/* Unmatched Claims */}
            {claims.length > 0 && claims.filter((claim) => !findClaimAvailabilityRow(planner, claim)).length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                <h4 className="mb-2 text-sm font-bold text-amber-900">
                  Unmatched Calendar Events ({claims.filter((claim) => !findClaimAvailabilityRow(planner, claim)).length})
                </h4>
                <p className="mb-4 text-xs text-amber-700">
                  These calendar listings did not automatically map to an appearance fee. Assign a fee type below to add them to the allocation tracker.
                </p>
                <div className="space-y-3">
                  {claims
                    .filter((claim) => !findClaimAvailabilityRow(planner, claim))
                    .map((claim) => (
                      <div key={claim.id} className="rounded-xl border border-amber-200 bg-white p-3 text-sm shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                          <div>
                            <div className="font-bold text-slate-900">{claim.date || "Unknown Date"}</div>
                            {claim.importSource && <div className="text-[10px] text-slate-500 uppercase tracking-wide">Source: {claim.importSource}</div>}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAppearanceClaim(claim.id)}
                            className="text-xs font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Remove
                          </button>
                        </div>
                        <div className="mb-3 text-xs text-slate-700 max-h-24 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-2 border border-slate-100">
                          {claim.notes || "No notes."}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">Assign to fee type:</span>
                          <div className="w-full sm:w-64">
                            <SelectField
                              value={claim.appearanceType === "Other" ? "" : claim.appearanceType}
                              onChange={(value) => updateAppearanceClaim(claim.id, "appearanceType", value)}
                              options={appearanceOptions}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
